import React, { useEffect, useState, useRef } from "react";
import MainLayout from "../../Layout/mainLayout";
import { ReactComponent as Add } from "../../Global Icons/adminIcon.svg";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import AddImage from "../../Global Functions/addImage";
import { 
  viewCountryListPagination, 
  viewCountry 
} from "../../Redux/Actions/settingsActions";
import { editAdmin, viewAdmin } from "../../Redux/Actions/adminActions";
import { getEnums } from "../../Redux/Actions/enumsActions";
import { ScrollTop, ToFirstCapital } from "../../Utils/helpers";
import { FaUser, FaEnvelope, FaPhone, FaBuilding } from "react-icons/fa";
import "../Reward Voucher/addReward.css";

const EditAdmins = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formState, setFormState] = useState({
    name: "",
    email: "",
    country_code: "",
    contact_number: "",
    role: "admin",
    timezone: "",
    department: "",
    profile_image: "",
    designation: "",
    address: "",
    id: "",
  });

  const [selectedCountry, setSelectedCountry] = useState("");
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { singleAdmin } = useSelector((state) => state.viewAdmin || {});
  const { countryList, country } = useSelector((state) => state.country || {});
  const { enumsData } = useSelector((state) => state.enumsData || {});

  // Load initial data
  useEffect(() => {
    dispatch(viewAdmin(id));
    dispatch(viewCountryListPagination());
    dispatch(getEnums());
    ScrollTop();
  }, [dispatch, id]);

  // Populate form with admin data
  useEffect(() => {
    if (singleAdmin?.data) {
      const adminData = singleAdmin.data;
      setFormState({
        ...adminData,
        id: id,
      });
      setSelectedCountry(adminData.country_id || "");
    }
  }, [singleAdmin, id]);

  // Update country code when country changes
  useEffect(() => {
    if (selectedCountry) {
      dispatch(viewCountry(selectedCountry));
    }
  }, [selectedCountry, dispatch]);

  useEffect(() => {
    if (country?.data?.country_phone_code) {
      setFormState(prev => ({ ...prev, country_code: country.data.country_phone_code }));
    }
  }, [country]);

  // Load timezone options
  useEffect(() => {
    const timeZones = enumsData?.data?.time_zones
      ? Object.entries(enumsData.data.time_zones)
          .filter(([countryName]) => countryName === country?.data?.country_name)
          .map(([countryName, tzObj]) => ({
            label: `${tzObj.timezone} (UTC${tzObj.offset_UTC})`,
            value: tzObj.timezone,
          }))
      : [];
    setTimezoneOptions(timeZones);
  }, [enumsData, country]);

  const departments = enumsData?.data?.department
    ? Object.values(enumsData.data.department)
    : [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const url = await AddImage(file);
      if (url) {
        setFormState(prev => ({ ...prev, profile_image: url }));
        toast.success("Profile image updated successfully!");
      }
    } catch {
      toast.error("Image upload failed");
    }
  };

  const validateForm = () => {
    if (!formState.name.trim()) return "Name is required";
    if (!formState.email.trim()) return "Email is required";
    if (!/^\S+@\S+\.\S+$/.test(formState.email)) return "Invalid email format";
    if (formState.contact_number.length !== 10) return "Contact number must be 10 digits";
    if (!selectedCountry) return "Please select a country";
    if (!formState.designation.trim()) return "Designation is required";
    if (!formState.department) return "Department is required";
    return null;
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await dispatch(editAdmin(formState));
      if (result.statusCode === 200) {
        toast.success("Admin updated successfully!");
        navigate("/manage_admins");
      }
    } catch (error) {
      toast.error("Failed to update admin");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="franchise-form-container">
        <div className="franchise-form-wrapper franchise-card">
          <div className="franchise-card-content">
            {/* Header */}
            <div className="franchise-header" style={{ display: "flex", gap: "25px"}}>
              <div className="franchise-header-icon">
                <Add />
              </div>
              <h1 style={{ marginTop: "15px"}}>Edit Admin</h1>
            </div>

            {/* Profile Image Upload */}
            <div className="form-section">
              <label className="form-label file-upload-area" htmlFor="profile_image">
                <FaUser style={{ fontSize: "2rem", marginBottom: "0.5rem" }} />
                <div>Profile Photo</div>
                <div className="file-upload-text">
                  Click to upload or drag and drop (JPG, PNG)
                </div>
              </label>
              <input
                ref={fileInputRef}
                id="profile_image"
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleImageUpload}
                className="form-input"
                style={{ display: "none" }}
              />
              {formState.profile_image && (
                <div style={{ textAlign: "center", marginTop: "1rem" }}>
                  <img
                    src={formState.profile_image}
                    alt="Profile preview"
                    style={{
                      width: "100%",
                      height: "300px",
                      // borderRadius: "50%",
                      objectFit: "cover",
                      border: "1px solid ",
                    }}
                  />
                  <p style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.5rem" }}>
                    Image updated ✓
                  </p>
                </div>
              )}
            </div>

            {/* 2-Column Grid - Name & Email */}
            <div className="form-grid-2">
              <div className="form-section">
                <label className="form-label">Full Name <span style={{color: "red"}}>*</span></label>
                <div className="input-icon-container">
                  <FaUser className="input-icon" />
                  <input
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    className="form-input with-icon"
                    placeholder="Enter full name"
                    required
                  />
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">Email <span style={{color: "red"}}>*</span></label>
                <div className="input-icon-container">
                  <FaEnvelope className="input-icon" />
                  <input
                    name="email"
                    type="email"
                    value={formState.email}
                    onChange={handleChange}
                    className="form-input with-icon"
                    placeholder="admin@company.com"
                    required
                  />
                </div>
              </div>
            </div>

            {/* 2-Column Grid - Contact & Country */}
            <div className="form-grid-2">
              <div className="form-section">
                <label className="form-label">Contact Number <span style={{color: "red"}}>*</span></label>
                <div className="input-icon-container">
                  <FaPhone className="input-icon" style={{ top: "35%"}}/>
                  <input
                    name="contact_number"
                    type="tel"
                    value={formState.contact_number}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormState(prev => ({ ...prev, contact_number: value }));
                    }}
                    maxLength={10}
                    className="form-input with-icon"
                    placeholder="10 digit number"
                  />
                  <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.25rem" }}>
                    {formState.contact_number.length}/10
                  </div>
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">Country <span style={{color: "red"}}>*</span></label>
                <select
                  name="country"
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Country</option>
                  {countryList?.data?.map((country) => (
                    <option key={country._id} value={country._id}>
                      {country.country_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* 2-Column Grid - Timezone & Department */}
            <div className="form-grid-2">
              <div className="form-section">
                <label className="form-label">Time Zone</label>
                <select
                  name="timezone"
                  value={formState.timezone}
                  onChange={handleChange}
                  disabled={!selectedCountry}
                  className="form-input"
                >
                  <option value="">Select Time Zone</option>
                  {timezoneOptions.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-section">
                <label className="form-label">Department <span style={{color: "red"}}>*</span></label>
                <select
                  name="department"
                  value={formState.department}
                  onChange={handleChange}
                  className="form-input"
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {ToFirstCapital(dept)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* 2-Column Grid - Designation */}
            <div className="form-section">
              <label className="form-label">Designation <span style={{color: "red"}}>*</span></label>
              <div className="input-icon-container">
                <FaBuilding className="input-icon" />
                <input
                  name="designation"
                  value={formState.designation}
                  onChange={handleChange}
                  className="form-input with-icon"
                  placeholder="e.g. Software Engineer"
                  required
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="form-navigation">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate("/manage_admins")}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${isSubmitting ? 'loading' : ''}`}
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{ boxShadow: "none"}}
              >
                {isSubmitting ? "Updating..." : "Update Admin"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditAdmins;
